/**
 * Mock data for the Requirements Tracking page.
 *
 * This is a UI prototype used to gather feedback from operations before the
 * backend exists. All data lives in-memory; interactions mutate local React
 * state only and reset on reload. The shapes here intentionally mirror the
 * planned API model (a `requirements` record plus append-only `versions` and
 * `events`) so the page can later swap mock data for real endpoints with
 * minimal churn.
 */

export type RequirementStatus =
  | 'draft'
  | 'submitted'
  | 'in_account_manager'
  | 'in_development'
  | 'fulfilled'
  | 'cancelled';

export type ActorRole = 'client' | 'account_manager' | 'developer';

export type RequirementPriority = 'low' | 'medium' | 'high' | 'urgent';

/** Immutable snapshot captured every time the requirement content changes. */
export interface RequirementVersion {
  version: number;
  title: string;
  body: string;
  /** Workflow stage the requirement was in at the moment it was edited. */
  statusAtChange: RequirementStatus;
  /** Optional author note describing what changed. */
  changeSummary?: string;
  changedBy: string;
  changedByRole: ActorRole;
  changedAt: string;
  /** True when this edit forced the workflow back to an earlier stage. */
  causedRevert?: boolean;
}

export type RequirementEventType =
  | 'created'
  | 'submitted'
  | 'status_changed'
  | 'content_reverted'
  | 'comment'
  | 'jira_updated'
  | 'attachment_added'
  | 'deletion_requested'
  | 'deletion_rejected'
  | 'cancelled'
  | 'fulfilled';

/** Append-only timeline entry: workflow transitions and discussion messages. */
export interface RequirementEvent {
  id: string;
  type: RequirementEventType;
  fromStatus?: RequirementStatus;
  toStatus?: RequirementStatus;
  body?: string;
  actor: string;
  actorRole: ActorRole;
  createdAt: string;
}

export interface Attachment {
  id: string;
  name: string;
  /** Size in bytes. */
  size: number;
  uploadedBy: string;
  uploadedByRole: ActorRole;
  uploadedAt: string;
}

/**
 * Development work is tracked in JIRA — JIRA is the system of record for the
 * build. The platform mirrors the linked issue's status so operations can see
 * progress without running JIRA reports, and knows when it's safe to fulfil.
 */
export type JiraStatus = 'to_do' | 'in_progress' | 'in_review' | 'done';

export interface JiraIssue {
  key: string;
  type: 'Story' | 'Task' | 'Bug' | 'Spike';
  summary: string;
  status: JiraStatus;
  assignee: string;
  url: string;
}

export const JIRA_FLOW: JiraStatus[] = ['to_do', 'in_progress', 'in_review', 'done'];

export const JIRA_STATUS_META: Record<
  JiraStatus,
  { label: string; color: 'gray' | 'blue' | 'yellow' | 'green' }
> = {
  to_do: { label: 'To Do', color: 'gray' },
  in_progress: { label: 'In Progress', color: 'blue' },
  in_review: { label: 'In Review', color: 'yellow' },
  done: { label: 'Done', color: 'green' },
};

/**
 * Records a cancellation/deletion. When `confirmed` is false the request is
 * pending operations sign-off; when true operations has finalised it and the
 * request becomes a read-only `cancelled` tombstone. `cancelledAtStatus` is the
 * workflow stage the request sat at when deletion was requested — the stepper
 * marks that stage in red.
 */
export interface Cancellation {
  requestedBy: string;
  requestedByRole: ActorRole;
  cancelledAtStatus: RequirementStatus;
  requestedAt: string;
  reason?: string;
  confirmed: boolean;
  confirmedBy?: string;
  confirmedAt?: string;
}

export interface Requirement {
  id: string;
  reference: string;
  title: string;
  body: string;
  clientName: string;
  clientCompany: string;
  status: RequirementStatus;
  currentVersion: number;
  priority: RequirementPriority;
  createdAt: string;
  updatedAt: string;
  versions: RequirementVersion[];
  events: RequirementEvent[];
  attachments: Attachment[];
  jiraIssues: JiraIssue[];
  cancellation?: Cancellation;
}

/** Ordered workflow stages shown in the stepper. `cancelled` is off-flow. */
export const STATUS_ORDER: RequirementStatus[] = [
  'draft',
  'in_account_manager',
  'in_development',
  'fulfilled',
];

export const STATUS_META: Record<
  RequirementStatus,
  { label: string; color: 'gray' | 'blue' | 'yellow' | 'orange' | 'green' | 'red'; owner: ActorRole }
> = {
  draft: { label: 'Draft', color: 'gray', owner: 'client' },
  submitted: { label: 'Submitted', color: 'blue', owner: 'account_manager' },
  in_account_manager: { label: 'In Account Manager Review', color: 'blue', owner: 'account_manager' },
  in_development: { label: 'In Development', color: 'orange', owner: 'developer' },
  fulfilled: { label: 'Fulfilled', color: 'green', owner: 'developer' },
  cancelled: { label: 'Cancelled', color: 'red', owner: 'account_manager' },
};

export const PRIORITY_META: Record<
  RequirementPriority,
  { label: string; color: 'gray' | 'blue' | 'yellow' | 'red' }
> = {
  low: { label: 'Low', color: 'gray' },
  medium: { label: 'Medium', color: 'blue' },
  high: { label: 'High', color: 'yellow' },
  urgent: { label: 'Urgent', color: 'red' },
};

export const mockRequirements: Requirement[] = [
  {
    id: 'req-1024',
    reference: 'REQ-1024',
    title: 'Nightly SFTP export of settlement files to Azure Blob',
    clientName: 'Dana Whitfield',
    clientCompany: 'Meridian Grid Services',
    status: 'in_development',
    currentVersion: 3,
    priority: 'high',
    createdAt: '2026-06-18T14:02:00Z',
    updatedAt: '2026-06-29T09:41:00Z',
    body: [
      'We need OneRing to pick up settlement files from our on-prem SFTP drop and',
      'land them in an Azure Blob container every night.',
      '',
      'Details:',
      '- Source: sftp://meridian-drop/outbound/settlements/*.csv',
      '- Target container: meridian-settlements (cool tier)',
      '- Schedule: nightly at 02:00 America/New_York',
      '- Filename must be preserved and prefixed with the ingest date (YYYYMMDD_).',
      '- On failure, email ops@meridiangrid.com AND raise a OneRing alert.',
      '- Retain source files for 7 days after a successful copy, then purge.',
    ].join('\n'),
    versions: [
      {
        version: 1,
        statusAtChange: 'draft',
        title: 'Nightly SFTP export of settlement files',
        changedBy: 'Dana Whitfield',
        changedByRole: 'client',
        changedAt: '2026-06-18T14:02:00Z',
        changeSummary: 'Initial draft',
        body: [
          'We need OneRing to pick up settlement files from our SFTP drop and copy',
          'them to Azure Blob every night.',
          '',
          '- Source: sftp://meridian-drop/outbound/settlements/*.csv',
          '- Target container: meridian-settlements',
          '- Schedule: nightly',
        ].join('\n'),
      },
      {
        version: 2,
        statusAtChange: 'in_account_manager',
        title: 'Nightly SFTP export of settlement files to Azure Blob',
        changedBy: 'Dana Whitfield',
        changedByRole: 'client',
        changedAt: '2026-06-24T16:20:00Z',
        causedRevert: true,
        changeSummary:
          'Added retention + failure-handling requirements after ops review call.',
        body: [
          'We need OneRing to pick up settlement files from our on-prem SFTP drop and',
          'land them in an Azure Blob container every night.',
          '',
          'Details:',
          '- Source: sftp://meridian-drop/outbound/settlements/*.csv',
          '- Target container: meridian-settlements (cool tier)',
          '- Schedule: nightly at 02:00 America/New_York',
          '- On failure, email ops@meridiangrid.com.',
          '- Retain source files for 7 days after a successful copy, then purge.',
        ].join('\n'),
      },
      {
        version: 3,
        statusAtChange: 'in_account_manager',
        title: 'Nightly SFTP export of settlement files to Azure Blob',
        changedBy: 'Dana Whitfield',
        changedByRole: 'client',
        changedAt: '2026-06-29T09:41:00Z',
        causedRevert: true,
        changeSummary:
          'Filename must be date-prefixed; also raise a OneRing alert on failure.',
        body: [
          'We need OneRing to pick up settlement files from our on-prem SFTP drop and',
          'land them in an Azure Blob container every night.',
          '',
          'Details:',
          '- Source: sftp://meridian-drop/outbound/settlements/*.csv',
          '- Target container: meridian-settlements (cool tier)',
          '- Schedule: nightly at 02:00 America/New_York',
          '- Filename must be preserved and prefixed with the ingest date (YYYYMMDD_).',
          '- On failure, email ops@meridiangrid.com AND raise a OneRing alert.',
          '- Retain source files for 7 days after a successful copy, then purge.',
        ].join('\n'),
      },
    ],
    events: [
      {
        id: 'e1',
        type: 'created',
        actor: 'Dana Whitfield',
        actorRole: 'client',
        createdAt: '2026-06-18T14:02:00Z',
        body: 'Requirement drafted.',
      },
      {
        id: 'e2',
        type: 'submitted',
        fromStatus: 'draft',
        toStatus: 'submitted',
        actor: 'Dana Whitfield',
        actorRole: 'client',
        createdAt: '2026-06-19T08:10:00Z',
      },
      {
        id: 'e3',
        type: 'status_changed',
        fromStatus: 'submitted',
        toStatus: 'in_account_manager',
        actor: 'Marcus Reed',
        actorRole: 'account_manager',
        createdAt: '2026-06-20T13:30:00Z',
      },
      {
        id: 'e4',
        type: 'comment',
        actor: 'Marcus Reed',
        actorRole: 'account_manager',
        createdAt: '2026-06-24T15:55:00Z',
        body:
          "What should happen on a failed transfer, and how long should we keep the source files?",
      },
      {
        id: 'e5',
        type: 'content_reverted',
        fromStatus: 'in_account_manager',
        toStatus: 'submitted',
        actor: 'Dana Whitfield',
        actorRole: 'client',
        createdAt: '2026-06-24T16:20:00Z',
        body: 'Edited v2 — added retention + failure handling. Sent back for re-review.',
      },
      {
        id: 'e6',
        type: 'status_changed',
        fromStatus: 'submitted',
        toStatus: 'in_account_manager',
        actor: 'Marcus Reed',
        actorRole: 'account_manager',
        createdAt: '2026-06-25T09:00:00Z',
      },
      {
        id: 'e7',
        type: 'content_reverted',
        fromStatus: 'in_account_manager',
        toStatus: 'submitted',
        actor: 'Dana Whitfield',
        actorRole: 'client',
        createdAt: '2026-06-29T09:41:00Z',
        body: 'Edited v3 — date-prefixed filenames + OneRing alert on failure.',
      },
      {
        id: 'e8',
        type: 'status_changed',
        fromStatus: 'submitted',
        toStatus: 'in_account_manager',
        actor: 'Marcus Reed',
        actorRole: 'account_manager',
        createdAt: '2026-06-29T11:15:00Z',
      },
      {
        id: 'e9',
        type: 'status_changed',
        fromStatus: 'in_account_manager',
        toStatus: 'in_development',
        actor: 'Marcus Reed',
        actorRole: 'account_manager',
        createdAt: '2026-06-29T11:20:00Z',
        body: 'Requirements are now stable. Handing off to the development team.',
      },
    ],
    attachments: [
      {
        id: 'a1',
        name: 'settlement-file-sample.csv',
        size: 48213,
        uploadedBy: 'Dana Whitfield',
        uploadedByRole: 'client',
        uploadedAt: '2026-06-18T14:05:00Z',
      },
      {
        id: 'a2',
        name: 'meridian-sftp-runbook.pdf',
        size: 231044,
        uploadedBy: 'Marcus Reed',
        uploadedByRole: 'account_manager',
        uploadedAt: '2026-06-20T13:35:00Z',
      },
    ],
    jiraIssues: [
      {
        key: 'ONERING-8342',
        type: 'Story',
        summary: 'Nightly SFTP → Azure Blob transfer for Meridian settlements',
        status: 'in_progress',
        assignee: 'Sofia Lindqvist',
        url: 'https://honeywell.atlassian.net/browse/ONERING-8342',
      },
    ],
  },
  {
    id: 'req-1031',
    reference: 'REQ-1031',
    title: 'Read-only Oracle reporting account for quarterly audit',
    clientName: 'Priya Nair',
    clientCompany: 'Cascade Power Cooperative',
    status: 'in_account_manager',
    currentVersion: 1,
    priority: 'medium',
    createdAt: '2026-06-27T10:15:00Z',
    updatedAt: '2026-06-27T10:40:00Z',
    body: [
      'Please provision a read-only Oracle account our external auditors can use',
      'during the Q3 audit window.',
      '',
      '- Access limited to the SETTLEMENTS and INVOICING schemas.',
      '- Active only between 2026-07-06 and 2026-07-24, then disabled.',
      '- MFA required; no ability to modify or export in bulk.',
    ].join('\n'),
    versions: [
      {
        version: 1,
        statusAtChange: 'draft',
        title: 'Read-only Oracle reporting account for quarterly audit',
        changedBy: 'Priya Nair',
        changedByRole: 'client',
        changedAt: '2026-06-27T10:15:00Z',
        changeSummary: 'Initial draft',
        body: [
          'Please provision a read-only Oracle account our external auditors can use',
          'during the Q3 audit window.',
          '',
          '- Access limited to the SETTLEMENTS and INVOICING schemas.',
          '- Active only between 2026-07-06 and 2026-07-24, then disabled.',
          '- MFA required; no ability to modify or export in bulk.',
        ].join('\n'),
      },
    ],
    events: [
      {
        id: 'e1',
        type: 'created',
        actor: 'Priya Nair',
        actorRole: 'client',
        createdAt: '2026-06-27T10:15:00Z',
      },
      {
        id: 'e2',
        type: 'submitted',
        fromStatus: 'draft',
        toStatus: 'submitted',
        actor: 'Priya Nair',
        actorRole: 'client',
        createdAt: '2026-06-27T10:40:00Z',
      },
    ],
    attachments: [
      {
        id: 'a1',
        name: 'auditor-access-scope.xlsx',
        size: 18422,
        uploadedBy: 'Priya Nair',
        uploadedByRole: 'client',
        uploadedAt: '2026-06-27T10:18:00Z',
      },
    ],
    jiraIssues: [],
  },
  {
    id: 'req-1035',
    reference: 'REQ-1035',
    title: 'Self-service dashboard for daily transfer volumes',
    clientName: 'Tom Ackerman',
    clientCompany: 'Northwind Energy Trading',
    status: 'draft',
    currentVersion: 1,
    priority: 'low',
    createdAt: '2026-06-30T17:05:00Z',
    updatedAt: '2026-06-30T17:05:00Z',
    body: [
      "We'd like a dashboard showing how many files we send and receive each day,",
      'with a rolling 30-day trend. Still fleshing out the exact metrics.',
    ].join('\n'),
    versions: [
      {
        version: 1,
        statusAtChange: 'draft',
        title: 'Self-service dashboard for daily transfer volumes',
        changedBy: 'Tom Ackerman',
        changedByRole: 'client',
        changedAt: '2026-06-30T17:05:00Z',
        changeSummary: 'Initial draft',
        body: [
          "We'd like a dashboard showing how many files we send and receive each day,",
          'with a rolling 30-day trend. Still fleshing out the exact metrics.',
        ].join('\n'),
      },
    ],
    events: [
      {
        id: 'e1',
        type: 'created',
        actor: 'Tom Ackerman',
        actorRole: 'client',
        createdAt: '2026-06-30T17:05:00Z',
      },
    ],
    attachments: [],
    jiraIssues: [],
  },
  {
    id: 'req-0998',
    reference: 'REQ-0998',
    title: 'PGP-encrypted invoice delivery to partner mailbox',
    clientName: 'Dana Whitfield',
    clientCompany: 'Meridian Grid Services',
    status: 'fulfilled',
    currentVersion: 2,
    priority: 'medium',
    createdAt: '2026-05-30T12:00:00Z',
    updatedAt: '2026-06-16T15:30:00Z',
    body: [
      'Deliver monthly invoices to our billing partner, PGP-encrypted with their',
      'public key, dropped to their SFTP inbox on the 1st of each month.',
      '',
      '- Encrypt with partner key fingerprint 4F2A ... 9C10.',
      '- Deliver to sftp://billing-partner/in/ on the 1st, 06:00 UTC.',
      '- Notify billing@meridiangrid.com on success and failure.',
    ].join('\n'),
    versions: [
      {
        version: 1,
        statusAtChange: 'draft',
        title: 'PGP-encrypted invoice delivery to partner mailbox',
        changedBy: 'Dana Whitfield',
        changedByRole: 'client',
        changedAt: '2026-05-30T12:00:00Z',
        changeSummary: 'Initial draft',
        body: [
          'Deliver monthly invoices to our billing partner, PGP-encrypted, dropped',
          'to their SFTP inbox on the 1st of each month.',
        ].join('\n'),
      },
      {
        version: 2,
        statusAtChange: 'in_account_manager',
        title: 'PGP-encrypted invoice delivery to partner mailbox',
        changedBy: 'Dana Whitfield',
        changedByRole: 'client',
        changedAt: '2026-06-03T11:10:00Z',
        causedRevert: true,
        changeSummary: 'Pinned the key fingerprint and exact delivery time.',
        body: [
          'Deliver monthly invoices to our billing partner, PGP-encrypted with their',
          'public key, dropped to their SFTP inbox on the 1st of each month.',
          '',
          '- Encrypt with partner key fingerprint 4F2A ... 9C10.',
          '- Deliver to sftp://billing-partner/in/ on the 1st, 06:00 UTC.',
          '- Notify billing@meridiangrid.com on success and failure.',
        ].join('\n'),
      },
    ],
    events: [
      {
        id: 'e1',
        type: 'created',
        actor: 'Dana Whitfield',
        actorRole: 'client',
        createdAt: '2026-05-30T12:00:00Z',
      },
      {
        id: 'e2',
        type: 'submitted',
        fromStatus: 'draft',
        toStatus: 'submitted',
        actor: 'Dana Whitfield',
        actorRole: 'client',
        createdAt: '2026-05-31T09:00:00Z',
      },
      {
        id: 'e3',
        type: 'status_changed',
        fromStatus: 'submitted',
        toStatus: 'in_account_manager',
        actor: 'Marcus Reed',
        actorRole: 'account_manager',
        createdAt: '2026-06-02T10:00:00Z',
      },
      {
        id: 'e4',
        type: 'content_reverted',
        fromStatus: 'in_account_manager',
        toStatus: 'submitted',
        actor: 'Dana Whitfield',
        actorRole: 'client',
        createdAt: '2026-06-03T11:10:00Z',
        body: 'Edited v2 — pinned key fingerprint and delivery time.',
      },
      {
        id: 'e5',
        type: 'status_changed',
        fromStatus: 'submitted',
        toStatus: 'in_account_manager',
        actor: 'Marcus Reed',
        actorRole: 'account_manager',
        createdAt: '2026-06-04T09:30:00Z',
      },
      {
        id: 'e6',
        type: 'status_changed',
        fromStatus: 'in_account_manager',
        toStatus: 'in_development',
        actor: 'Marcus Reed',
        actorRole: 'account_manager',
        createdAt: '2026-06-05T14:00:00Z',
      },
      {
        id: 'e7',
        type: 'fulfilled',
        fromStatus: 'in_development',
        toStatus: 'fulfilled',
        actor: 'Sofia Lindqvist',
        actorRole: 'developer',
        createdAt: '2026-06-16T15:30:00Z',
        body: 'Deployed to production and verified two delivery cycles.',
      },
    ],
    attachments: [
      {
        id: 'a1',
        name: 'partner-public-key.asc',
        size: 3126,
        uploadedBy: 'Dana Whitfield',
        uploadedByRole: 'client',
        uploadedAt: '2026-05-30T12:04:00Z',
      },
    ],
    jiraIssues: [
      {
        key: 'ONERING-8190',
        type: 'Story',
        summary: 'PGP-encrypted invoice delivery to billing partner SFTP',
        status: 'done',
        assignee: 'Sofia Lindqvist',
        url: 'https://honeywell.atlassian.net/browse/ONERING-8190',
      },
    ],
  },
  ...buildFillerRequirements(),
];

/**
 * Additional lightweight mock requirements so the list is long enough to
 * exercise the scroll behaviour. These deliberately carry a single version and
 * a minimal event trail — they exist to fill the list, not to demo the full
 * change-history flow.
 */
function buildFillerRequirements(): Requirement[] {
  const seeds: Array<{
    id: string;
    title: string;
    clientName: string;
    clientCompany: string;
    status: RequirementStatus;
    priority: RequirementPriority;
    createdAt: string;
    updatedAt: string;
    summary: string;
    jira?: JiraIssue['status'];
  }> = [
    {
      id: '1042',
      title: 'Bulk CSV upload endpoint for meter readings',
      clientName: 'Elena Fischer',
      clientCompany: 'Alpine Utilities AG',
      status: 'in_account_manager',
      priority: 'high',
      createdAt: '2026-06-28T09:12:00Z',
      updatedAt: '2026-06-28T09:12:00Z',
      summary:
        'Provide a bulk upload endpoint so we can push daily meter reading CSVs instead of per-record API calls.',
    },
    {
      id: '1045',
      title: 'SSO integration with our Azure AD tenant',
      clientName: 'Raymond Cole',
      clientCompany: 'Harbor Point Energy',
      status: 'in_account_manager',
      priority: 'urgent',
      createdAt: '2026-06-26T15:40:00Z',
      updatedAt: '2026-07-01T11:05:00Z',
      summary:
        'Enable SAML/OIDC SSO against our Azure AD so staff can use corporate credentials.',
    },
    {
      id: '1048',
      title: 'Automated weekly reconciliation report by email',
      clientName: 'Grace Okonkwo',
      clientCompany: 'Delta Metering Co.',
      status: 'in_development',
      priority: 'medium',
      createdAt: '2026-06-22T08:30:00Z',
      updatedAt: '2026-07-02T10:00:00Z',
      summary:
        'Email a weekly reconciliation report comparing sent vs. acknowledged settlement files.',
      jira: 'in_progress',
    },
    {
      id: '1051',
      title: 'Retention policy override for regulatory hold',
      clientName: 'Priya Nair',
      clientCompany: 'Cascade Power Cooperative',
      status: 'draft',
      priority: 'low',
      createdAt: '2026-07-01T13:20:00Z',
      updatedAt: '2026-07-01T13:20:00Z',
      summary:
        'Ability to place specific file sets under a regulatory hold that overrides the standard purge schedule.',
    },
    {
      id: '1054',
      title: 'Webhook notifications for transfer completion',
      clientName: 'Tom Ackerman',
      clientCompany: 'Northwind Energy Trading',
      status: 'in_account_manager',
      priority: 'medium',
      createdAt: '2026-06-30T10:05:00Z',
      updatedAt: '2026-06-30T10:05:00Z',
      summary:
        'Fire a webhook to our endpoint whenever a scheduled transfer completes or fails.',
    },
    {
      id: '1057',
      title: 'Dedicated staging SFTP environment',
      clientName: 'Marco Bianchi',
      clientCompany: 'Adriatic Grid S.p.A.',
      status: 'in_account_manager',
      priority: 'high',
      createdAt: '2026-06-24T14:50:00Z',
      updatedAt: '2026-06-29T16:10:00Z',
      summary:
        'Stand up a non-production SFTP environment we can use to validate file formats before go-live.',
    },
    {
      id: '1060',
      title: 'Two-factor requirement for all partner accounts',
      clientName: 'Sarah Nguyen',
      clientCompany: 'Pacific Rim Power',
      status: 'in_development',
      priority: 'urgent',
      createdAt: '2026-06-20T09:00:00Z',
      updatedAt: '2026-07-02T14:20:00Z',
      summary:
        'Enforce MFA for every external partner account accessing the portal or SFTP.',
      jira: 'in_review',
    },
    {
      id: '1063',
      title: 'Historical data export for decommissioned feed',
      clientName: 'Dana Whitfield',
      clientCompany: 'Meridian Grid Services',
      status: 'fulfilled',
      priority: 'low',
      createdAt: '2026-05-18T11:30:00Z',
      updatedAt: '2026-06-10T09:15:00Z',
      summary:
        'One-time export of all historical transfers for a feed we are retiring, delivered as a single archive.',
      jira: 'done',
    },
    {
      id: '1066',
      title: 'Configurable alert thresholds per client',
      clientName: 'Elena Fischer',
      clientCompany: 'Alpine Utilities AG',
      status: 'in_account_manager',
      priority: 'medium',
      createdAt: '2026-07-02T08:45:00Z',
      updatedAt: '2026-07-02T08:45:00Z',
      summary:
        'Let each client set their own alerting thresholds for late or missing transfers.',
    },
  ];

  const mapped: Requirement[] = seeds.map((s) => ({
    id: `req-${s.id}`,
    reference: `REQ-${s.id}`,
    title: s.title,
    body: s.summary,
    clientName: s.clientName,
    clientCompany: s.clientCompany,
    status: s.status,
    currentVersion: 1,
    priority: s.priority,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
    versions: [
      {
        version: 1,
        statusAtChange: 'draft',
        title: s.title,
        body: s.summary,
        changedBy: s.clientName,
        changedByRole: 'client',
        changedAt: s.createdAt,
        changeSummary: 'Initial draft',
      },
    ],
    events: [
      {
        id: `${s.id}-e1`,
        type: 'created',
        actor: s.clientName,
        actorRole: 'client',
        createdAt: s.createdAt,
        body: 'Requirement drafted.',
      },
    ],
    attachments: [],
    jiraIssues: s.jira
      ? [
          {
            key: `ONERING-${8200 + Number(s.id)}`,
            type: 'Story',
            summary: s.title,
            status: s.jira,
            assignee: 'Sofia Lindqvist',
            url: `https://honeywell.atlassian.net/browse/ONERING-${8200 + Number(s.id)}`,
          },
        ]
      : [],
  }));

  const cancellationExamples: Requirement[] = [
    {
      id: 'req-1069',
      reference: 'REQ-1069',
      title: 'Duplicate SFTP feed for archived contract (withdrawn)',
      body: 'Set up a second SFTP feed mirroring the primary settlement drop. Requested in error — contract was archived.',
      clientName: 'Raymond Cole',
      clientCompany: 'Harbor Point Energy',
      status: 'in_account_manager',
      currentVersion: 1,
      priority: 'low',
      createdAt: '2026-06-29T09:00:00Z',
      updatedAt: '2026-07-03T10:20:00Z',
      versions: [
        {
          version: 1,
          statusAtChange: 'draft',
          title: 'Duplicate SFTP feed for archived contract',
          body: 'Set up a second SFTP feed mirroring the primary settlement drop.',
          changedBy: 'Raymond Cole',
          changedByRole: 'client',
          changedAt: '2026-06-29T09:00:00Z',
          changeSummary: 'Initial draft',
        },
      ],
      events: [
        {
          id: '1069-e1',
          type: 'created',
          actor: 'Raymond Cole',
          actorRole: 'client',
          createdAt: '2026-06-29T09:00:00Z',
          body: 'Requirement drafted.',
        },
        {
          id: '1069-e2',
          type: 'deletion_requested',
          actor: 'Raymond Cole',
          actorRole: 'client',
          createdAt: '2026-07-03T10:20:00Z',
          body: 'Requested deletion — contract was archived, feed no longer needed.',
        },
      ],
      attachments: [],
      jiraIssues: [],
      cancellation: {
        requestedBy: 'Raymond Cole',
        requestedByRole: 'client',
        cancelledAtStatus: 'in_account_manager',
        requestedAt: '2026-07-03T10:20:00Z',
        reason: 'Contract archived — feed no longer needed.',
        confirmed: false,
      },
    },
    {
      id: 'req-1012',
      reference: 'REQ-1012',
      title: 'Legacy CSV drop for retired billing system',
      body: 'Nightly CSV drop for our old billing system. Project cancelled after the billing system was decommissioned.',
      clientName: 'Grace Okonkwo',
      clientCompany: 'Delta Metering Co.',
      status: 'cancelled',
      currentVersion: 1,
      priority: 'medium',
      createdAt: '2026-05-22T12:00:00Z',
      updatedAt: '2026-06-14T15:00:00Z',
      versions: [
        {
          version: 1,
          statusAtChange: 'draft',
          title: 'Legacy CSV drop for retired billing system',
          body: 'Nightly CSV drop for our old billing system.',
          changedBy: 'Grace Okonkwo',
          changedByRole: 'client',
          changedAt: '2026-05-22T12:00:00Z',
          changeSummary: 'Initial draft',
        },
      ],
      events: [
        {
          id: '1012-e1',
          type: 'created',
          actor: 'Grace Okonkwo',
          actorRole: 'client',
          createdAt: '2026-05-22T12:00:00Z',
          body: 'Requirement drafted.',
        },
        {
          id: '1012-e2',
          type: 'status_changed',
          fromStatus: 'submitted',
          toStatus: 'in_account_manager',
          actor: 'Marcus Reed',
          actorRole: 'account_manager',
          createdAt: '2026-05-28T09:30:00Z',
        },
        {
          id: '1012-e3',
          type: 'deletion_requested',
          actor: 'Grace Okonkwo',
          actorRole: 'client',
          createdAt: '2026-06-13T11:00:00Z',
          body: 'Requested deletion — billing system decommissioned.',
        },
        {
          id: '1012-e4',
          type: 'cancelled',
          actor: 'Marcus Reed',
          actorRole: 'account_manager',
          createdAt: '2026-06-14T15:00:00Z',
          body: 'Deletion confirmed by account manager. Request cancelled.',
        },
      ],
      attachments: [],
      jiraIssues: [],
      cancellation: {
        requestedBy: 'Grace Okonkwo',
        requestedByRole: 'client',
        cancelledAtStatus: 'in_account_manager',
        requestedAt: '2026-06-13T11:00:00Z',
        reason: 'Billing system decommissioned.',
        confirmed: true,
        confirmedBy: 'Marcus Reed',
        confirmedAt: '2026-06-14T15:00:00Z',
      },
    },
  ];

  return [...mapped, ...cancellationExamples];
}
